import axios from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { getUIColorPalette } from "../../../theme/ColorPalette";
import { convertGeocodeItemToSearchResultItem } from "../../../tools/GeocodeTools";
import type { BikeStationRentalUrisDTO, BikeStationStatusDTO } from "../../../types/dtos/api/bike-station.dto";
import { GeocodeSource, GeocodeType, type SearchResultItem } from "../../../types/geocode-ui.types";
import { resolveBookingUrlFromBookingUrls } from "../../ItinerariesPlanner/itineraryDetails/offerPricing/helpers/OfferPricingUrl.helper";

import batteryFull from "../../../image/icons/battery/batteryFull.svg";
import batteryHigh from "../../../image/icons/battery/batteryHigh.svg";
import batteryLow from "../../../image/icons/battery/batteryLow.svg";
import batteryMedium from "../../../image/icons/battery/batteryMedium.svg";

import bikeIcon from "../../../image/icons/map/transportIcons/BicycleIcon.svg";
import ebikeIcon from "../../../image/icons/map/transportIcons/EBicycleIcon.svg";
import { resolveBikeStationDisplayName } from "./bikeStationPopupModern.tools";

const VEHICLE_TYPE_ICONS: Record<string, string> = {
    "bike":  bikeIcon,
    "ebike": ebikeIcon,
};

interface BikeStationPopupProps {
    "visible":       boolean;
    "x":             number;
    "y":             number;
    "name":          string;
    "stationId":     string;
    "operator":      string;
    "operatorName"?: string;
    "rentalUris"?:   BikeStationRentalUrisDTO;
    "lngLat":        [number, number];
    "isDarkUi":      boolean;
    "onClose":       () => void;
    "onSetArrival"?: ( item: SearchResultItem ) => void;
}

const getBatteryIcon = ( percent: number ): string => {
    if ( percent <= 0.25 ) {
        return batteryLow;
    }
    if ( percent <= 0.5 )  {
        return batteryMedium;
    }
    if ( percent <= 0.75 ) {
        return batteryHigh;
    }

    return batteryFull;
};

export const BikeStationPopup: React.FC<BikeStationPopupProps> = ( {
    visible,
    x,
    y,
    name,
    stationId,
    operator,
    operatorName,
    rentalUris,
    lngLat,
    isDarkUi,
    onClose,
    onSetArrival,
} ) => {
    const palette  = getUIColorPalette( isDarkUi );
    const menuRef  = useRef<HTMLDivElement | null>( null );
    const displayName = resolveBikeStationDisplayName( name );
    const displayOperatorName = resolveBikeStationDisplayName( operatorName );
    const rentalUrl = rentalUris != null
        ? resolveBookingUrlFromBookingUrls( rentalUris as Record<string, string> )
        : null;
    const [ fetchState, setFetchState ] = useState<{
        "loading": boolean;
        "status":  BikeStationStatusDTO | null;
    }>( {
        "loading": false,
        "status":  null,
    } );

    useEffect( () => {
        if ( !visible || stationId === "" ) {
            return;
        }

        const fetchStatus = async(): Promise<void> => {
            setFetchState( {
                "loading": true,
                "status":  null,
            } );

            try {
                const response = await axios.get<BikeStationStatusDTO>( `/api/pois/bike-stations/${ encodeURIComponent( operator ) }/${ encodeURIComponent( stationId ) }` );
                setFetchState( {
                    "loading": false,
                    "status":  response.data,
                } );
            } catch {
                setFetchState( {
                    "loading": false,
                    "status":  null,
                } );
            }
        };

        void fetchStatus();
    }, [ visible, stationId, operator ] );

    const buildItem = useCallback( (): SearchResultItem => {
        const geocodeItem = {
            "type":     GeocodeType.ADDRESS,
            "id":       `bike-${ lngLat[1] }-${ lngLat[0] }`,
            "result":   displayName,
            "source":   GeocodeSource.PELIAS,
            "location": {
                "coordinates": {
                    "lat": lngLat[1],
                    "lon": lngLat[0],
                },
            },
        };

        return convertGeocodeItemToSearchResultItem( geocodeItem );
    }, [ lngLat, displayName ] );

    if ( !visible ) {
        return null;
    }

    const clampedX = Math.max( 8, Math.min( x, window.innerWidth - 240 ) );
    const clampedY = Math.max( 8, Math.min( y, window.innerHeight - 200 ) );

    return (
        <>
            <div
                className="absolute inset-0 z-20"
                onClick={onClose}
                onTouchStart={onClose}
                style={{ "background": "transparent" }}
            />
            <div
                className="absolute z-30 min-w-56 rounded-xl border backdrop-blur-md shadow-xl"
                onMouseLeave={onClose}
                ref={menuRef}
                style={{
                    "top":         clampedY,
                    "left":        clampedX,
                    "borderColor": palette.overlayBorder,
                    "background":  palette.overlayCardBackgroundStrong,
                    "color":       palette.overlayTextPrimary,
                }}
            >
                {/* Header */}
                <div
                    className="px-4 py-3 border-b text-sm font-medium"
                    style={{
                        "borderColor": palette.overlayBorder,
                        "color":       palette.overlayTextPrimary,
                    }}
                >
                    <div>
                        {displayName}
                    </div>
                    <div
                        className="text-xs mt-0.5"
                        style={{ "color": palette.overlayTextSecondary }}
                    >
                        {rentalUrl != null
                            ? (
                                <span
                                    onClick={() => {
                                        window.open( rentalUrl, "_blank", "noopener,noreferrer" );
                                    }}
                                    onKeyDown={( keyboardEvent ) => {
                                        if ( keyboardEvent.key === "Enter" || keyboardEvent.key === " " ) {
                                            keyboardEvent.preventDefault();
                                            window.open( rentalUrl, "_blank", "noopener,noreferrer" );
                                        }
                                    }}
                                    role="link"
                                    style={{
                                        "cursor":         "pointer",
                                        "textDecoration": "underline",
                                    }}
                                    tabIndex={0}
                                >
                                    {displayOperatorName || operator}
                                </span>
                            )
                            : displayOperatorName || operator}
                    </div>
                </div>
                {/* Bikes available */}
                <div
                    className="px-4 py-4 text-sm border-b"
                    style={{
                        "color":       palette.overlayTextSecondary,
                        "borderColor": palette.overlayBorder,
                    }}
                >
                    {fetchState.loading
                        ? "Chargement..."
                        : fetchState.status === null
                            ? "Disponibilité inconnue"
                            : fetchState.status.availabilitySummary.types.length > 0
                                ? (
                                    <div className="flex flex-col gap-1">
                                        {fetchState.status.availabilitySummary.types.map( ( v ) => (
                                            <div
                                                className="flex items-center gap-2"
                                                key={v.vehicleTypeId}
                                            >
                                                <img
                                                    alt={v.vehicleTypeId}
                                                    src={VEHICLE_TYPE_ICONS[v.vehicleTypeId] ?? v.vehicleTypeId}
                                                    style={{
                                                        "filter": isDarkUi ? "brightness(0) invert(1)" : "brightness(0)",
                                                        "width":  "35px",
                                                        "height": "35px",
                                                    }}
                                                />
                                                <span>
                                                    {v.count}
                                                    {" "}
                                                    x
                                                </span>
                                                {v.vehicleTypeId === "ebike" && fetchState.status?.availabilitySummary.maxAvailableBattery !== null && fetchState.status?.availabilitySummary.maxAvailableBattery !== undefined && (
                                                    <img
                                                        alt="battery"
                                                        className="ml-1 inline-block"
                                                        src={getBatteryIcon( fetchState.status.availabilitySummary.maxAvailableBattery )}
                                                        style={{
                                                            "filter": isDarkUi ? "invert(1)" : undefined,
                                                            "width":  "20px",
                                                            "height": "20px",
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        ) )}
                                    </div>
                    )
                    : `${ fetchState.status.availabilitySummary.bikes ?? 0 } vélo(s) disponible(s)`}
                </div>
                {/* Action */}
                <div>
                    <button
                        className="w-full text-center font-semibold px-4 py-2 text-sm"
                        onClick={() => {
                            onSetArrival?.( buildItem() );
                            onClose();
                        }}
                        onMouseEnter={( e ) => {
                            e.currentTarget.style.background = palette.interactiveHoverBackground;
                        }}
                        onMouseLeave={( e ) => {
                            e.currentTarget.style.background = "transparent";
                        }}
                        style={{ "background": "transparent" }}
                    >
                        S&apos;y rendre
                    </button>
                </div>
            </div>
        </>
    );
};
